/**
 * Cross-repo marketing attribution — the app-side half of the cookie contract
 * the marketing site (founding-dev-landing) writes when someone clicks an ad
 * or a UTM'd link before ever reaching app.founding.dev. Both repos read/write
 * the SAME two cookies so a person who lands on the marketing site and later
 * signs up in the app still carries their first/last touch.
 *
 * Cookies: `_founding_attr_first` / `_founding_attr_last`, value =
 * `encodeURIComponent(JSON.stringify(record))`. First-touch is set once and
 * never overwritten; last-touch is replaced whenever a newer touch shows up
 * (guarded by `captured_at`, so an out-of-order write can't clobber a fresher
 * one). 30 days, `path=/`, `SameSite=Lax`, `Domain=.founding.dev` in prod so
 * app.founding.dev and the marketing site share the same cookie jar.
 *
 * Anti-poisoning: a bare navigation with no tracking param and no external
 * referrer (an internal link, a bookmark, a refresh) writes NOTHING — it
 * would otherwise overwrite a real touch with an empty one.
 */

/** The 16 attribution fields, in the order the shared contract defines them.
 *  `captured_at` is tracked separately (see AttributionTouch) since every
 *  touch requires it, unlike these — which are all optional per touch. */
export const ATTR_FIELDS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'twclid',
  'ttclid',
  'rdt_cid',
  'ref',
  'referrer',
  'landing_page',
  'ga_client_id',
] as const;

export type AttrField = typeof ATTR_FIELDS[number];

export type AttributionTouch = Partial<Record<AttrField, string>> & {
  captured_at: string;
};

export interface AttributionReport {
  first?: AttributionTouch;
  last?: AttributionTouch;
  ga_client_id?: string;
  ph_did?: string;
  cta?: string;
}

const FIRST_TOUCH_COOKIE = '_founding_attr_first';
const LAST_TOUCH_COOKIE = '_founding_attr_last';
const COOKIE_MAX_AGE_SEC = 30 * 24 * 60 * 60; // 30 days

const CTA_STORAGE_KEY = 'fd_cta';
const PH_DID_STORAGE_KEY = 'fd_ph_did';

// The 12 URL params that count as "a tracking link brought this person here":
// the 5 UTMs + the 6 ad-network click ids + our own `ref` param. `referrer`,
// `landing_page` and `ga_client_id` are derived, never read off the URL.
const TRACKING_PARAM_FIELDS: readonly AttrField[] = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'twclid',
  'ttclid',
  'rdt_cid',
  'ref',
];

function isInternalReferrerHost(hostname: string): boolean {
  return hostname === 'founding.dev' || hostname.endsWith('.founding.dev');
}

/** The external referrer's hostname only ("google.com"), or null when the
 *  referrer is absent, unparseable, or internal to founding.dev. */
function externalReferrerHostname(): string | null {
  const ref = document.referrer;
  if (!ref) return null;
  try {
    const hostname = new URL(ref).hostname;
    if (!hostname || isInternalReferrerHost(hostname)) return null;
    return hostname;
  } catch {
    return null;
  }
}

function readCookie(name: string): string | null {
  try {
    const escaped = name.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&');
    const match = document.cookie.match(new RegExp('(?:^|; )' + escaped + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

/** Empty on localhost/IP (host-only cookie); `; Domain=.founding.dev`
 *  everywhere else so the marketing site and the app share the cookie. */
function cookieDomainAttr(): string {
  const host = window.location.hostname;
  const isLocal = host === 'localhost' || host === '127.0.0.1' || /^[0-9.]+$/.test(host);
  return isLocal ? '' : '; Domain=.founding.dev';
}

function writeCookie(name: string, value: string): void {
  try {
    document.cookie =
      `${name}=${encodeURIComponent(value)}; Max-Age=${COOKIE_MAX_AGE_SEC}; Path=/; SameSite=Lax` +
      cookieDomainAttr();
  } catch {
    /* attribution capture must never break app boot */
  }
}

/** Tolerant read: a missing or corrupt cookie is treated as "no touch yet". */
function readTouchCookie(name: string): AttributionTouch | null {
  const raw = readCookie(name);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && typeof parsed.captured_at === 'string') {
      return parsed as AttributionTouch;
    }
    return null;
  } catch {
    return null;
  }
}

/** GA's `_ga` cookie is `GA1.<domain-depth>.<id-part-1>.<id-part-2>`; the
 *  client id GA/GA4 actually key on is the last two dot-separated parts. */
function readGaClientId(): string | null {
  const raw = readCookie('_ga');
  if (!raw) return null;
  const parts = raw.split('.');
  if (parts.length >= 4) return `${parts[parts.length - 2]}.${parts[parts.length - 1]}`;
  return raw;
}

function mergeFirstTouch(record: AttributionTouch): void {
  if (readTouchCookie(FIRST_TOUCH_COOKIE)) return; // set-once
  writeCookie(FIRST_TOUCH_COOKIE, JSON.stringify(record));
}

function mergeLastTouch(record: AttributionTouch): void {
  const existing = readTouchCookie(LAST_TOUCH_COOKIE);
  // Skip only when the existing touch is STRICTLY newer — a corrupt/absent
  // cookie or an equal timestamp both fall through to overwrite.
  if (existing && Date.parse(existing.captured_at) > Date.parse(record.captured_at)) return;
  writeCookie(LAST_TOUCH_COOKIE, JSON.stringify(record));
}

/**
 * Read the current URL + referrer and merge a touch into the shared cookies.
 * Call once per app boot. Safe to call repeatedly — cookie logic already
 * guards first-touch immutability and last-touch ordering.
 */
export function captureAttribution(): void {
  try {
    const params = new URLSearchParams(window.location.search);

    // cta / ph_did — captured independently of the anti-poisoning gate below,
    // since they're this app's own params, not marketing attribution.
    const cta = params.get('cta');
    if (cta) {
      try { sessionStorage.setItem(CTA_STORAGE_KEY, cta); } catch { /* ignore */ }
    }
    const phDid = params.get('ph_did');
    if (phDid) {
      try { sessionStorage.setItem(PH_DID_STORAGE_KEY, phDid); } catch { /* ignore */ }
    }

    const trackingParams: Partial<Record<AttrField, string>> = {};
    for (const field of TRACKING_PARAM_FIELDS) {
      const value = params.get(field);
      if (value) trackingParams[field] = value;
    }
    const hasTrackingParam = Object.keys(trackingParams).length > 0;
    const referrerHostname = externalReferrerHostname();

    // Anti-poisoning: nothing to attribute this visit to → write nothing,
    // rather than overwrite a real touch with an empty one.
    if (!hasTrackingParam && !referrerHostname) return;

    const record: AttributionTouch = {
      ...trackingParams,
      captured_at: new Date().toISOString(),
      // Never the query string — /auth/callback?token=... must not persist.
      landing_page: window.location.origin + window.location.pathname,
    };
    if (referrerHostname) record.referrer = referrerHostname;
    const gaClientId = readGaClientId();
    if (gaClientId) record.ga_client_id = gaClientId;

    mergeFirstTouch(record);
    mergeLastTouch(record);
  } catch {
    /* attribution capture must never break app boot */
  }
}

/** Assemble the belt-and-braces report POSTed to /api/attribution — cookies
 *  can be capped/cleared (Safari ITP) before the user ever signs up, so the
 *  server persists this onto the User doc as a durable backstop. */
export function buildAttributionReport(): AttributionReport {
  const report: AttributionReport = {};

  const first = readTouchCookie(FIRST_TOUCH_COOKIE);
  if (first) report.first = first;

  const last = readTouchCookie(LAST_TOUCH_COOKIE);
  if (last) report.last = last;

  const gaClientId = readGaClientId();
  if (gaClientId) report.ga_client_id = gaClientId;

  try {
    const cta = sessionStorage.getItem(CTA_STORAGE_KEY);
    if (cta) report.cta = cta;
  } catch { /* ignore */ }

  try {
    const phDid = sessionStorage.getItem(PH_DID_STORAGE_KEY);
    if (phDid) report.ph_did = phDid;
  } catch { /* ignore */ }

  return report;
}
